import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Building2, AlertCircle, CheckCircle, Loader } from 'lucide-react';

const VerifyEmail: React.FC = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [loading, setLoading] = useState(true);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const getApiBaseUrl = (): string => {
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    const protocol = window.location.protocol;

    // Special handling for WebContainer preview URLs
    if (hostname.includes('webcontainer.io') || hostname.includes('webcontainer-api.io')) {
      const baseUrl = hostname.replace('--5173--', '--5002--');
      return `${protocol}//${baseUrl}/api`;
    }

    // Local development
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return `${protocol}//${hostname}:5002/api`;
    }

    // Render or other deployment domains
    return `${protocol}//${hostname}/api`;
  }

  // Fallback to env or default
  return import.meta.env.VITE_API_URL || 'http://localhost:5002/api';
};


  const API_BASE_URL = getApiBaseUrl( )

  useEffect(() => {
    const verifyEmail = async () => {
      if (!token) {
        setError('Invalid verification link');
        setLoading(false);
        return;
      }

      try {
        const response = await fetch(`${API_BASE_URL}/auth/verify-email`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ token })
        });

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.message || 'Verification failed');
        }

        setSuccess(true);
      } catch (err: any) {
        setError(err.message || 'Verification failed');
      } finally {
        setLoading(false);
      }
    };

    verifyEmail();
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-white to-purple-50">
        <div className="max-w-md w-full text-center">
          <div className="flex items-center justify-center">
            <div className="flex items-center space-x-2">
              <Building2 className="w-10 h-10 text-blue-600" />
              <span className="text-2xl font-bold text-gray-900">AetherFlow</span>
            </div>
          </div>
          
          <div className="mt-8">
            <Loader className="w-12 h-12 text-blue-600 animate-spin mx-auto" />
            <h2 className="mt-4 text-xl font-semibold text-gray-900">
              Verifying your email...
            </h2>
            <p className="mt-2 text-gray-600">
              Please wait while we verify your email address.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-white to-purple-50">
        <div className="max-w-md w-full text-center">
          <div className="flex items-center justify-center">
            <div className="flex items-center space-x-2">
              <Building2 className="w-10 h-10 text-blue-600" />
              <span className="text-2xl font-bold text-gray-900">AetherFlow</span>
            </div>
          </div>
          
          <div className="mt-8">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            
            <h2 className="mt-6 text-2xl font-bold text-gray-900">
              Email Verified Successfully!
            </h2>
            
            <p className="mt-2 text-gray-600">
              Your email address has been verified. You can now access all features of your account.
            </p>
            
            <div className="mt-8">
              <Link
                to="/login"
                className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-lg text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200"
              >
                Continue to Dashboard
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-white to-purple-50">
      <div className="max-w-md w-full text-center">
        <div className="flex items-center justify-center">
          <div className="flex items-center space-x-2">
            <Building2 className="w-10 h-10 text-blue-600" />
            <span className="text-2xl font-bold text-gray-900">AetherFlow</span>
          </div>
        </div>
        
        <div className="mt-8 bg-red-50 border border-red-200 rounded-lg p-6">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-red-800 mb-2">Verification Failed</h3>
          <p className="text-red-600 mb-4">
            {error || 'This verification link is invalid or has expired.'}
          </p>
          <div className="space-y-2">
            <Link
              to="/login"
              className="block text-blue-600 hover:text-blue-500 font-medium"
            >
              Go to Login
            </Link>
            <Link
              to="/register"
              className="block text-blue-600 hover:text-blue-500 font-medium"
            >
              Create New Account
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VerifyEmail;